import React, { useEffect, useRef } from 'react';

const ShaderBackground = () => {
  const canvasRef = useRef(null);

  const vsSource = `
    attribute vec4 aVertexPosition;
    void main() {
      gl_Position = aVertexPosition;
    }
  `;

  // Flowman colour palette:
  //   bgColor1  #0D1F38  → vec4(0.051, 0.122, 0.220, 1.0)
  //   bgColor2  #061525  → vec4(0.024, 0.082, 0.145, 1.0)
  //   lineColor #10A7F3  → vec4(0.063, 0.655, 0.953, 1.0)
  const fsSource = `
    precision highp float;
    uniform vec2 iResolution;
    uniform float iTime;
    uniform vec2 iMouse;
    uniform float iPointerActive;

    const float overallSpeed     = 0.15;
    const float gridSmoothWidth  = 0.003;
    const float axisWidth        = 0.05;
    const float majorLineWidth   = 0.025;
    const float minorLineWidth   = 0.0125;
    const float majorLineFrequency = 5.0;
    const float minorLineFrequency = 1.0;
    const vec4  gridColor        = vec4(0.5);
    const float scale            = 5.0;
    const vec4  lineColor        = vec4(0.063, 0.655, 0.953, 1.0);
    const vec4  accentColor      = vec4(0.062, 0.953, 0.839, 1.0);
    const float minLineWidth     = 0.014;
    const float maxLineWidth     = 0.24;
    const float lineSpeed        = 1.0  * overallSpeed;
    const float lineAmplitude    = 1.0;
    const float lineFrequency    = 0.2;
    const float warpSpeed        = 0.2  * overallSpeed;
    const float warpFrequency    = 0.5;
    const float warpAmplitude    = 1.0;
    const float offsetFrequency  = 0.5;
    const float offsetSpeed      = 1.33 * overallSpeed;
    const float minOffsetSpread  = 0.6;
    const float maxOffsetSpread  = 2.0;
    const int   linesPerGroup    = 16;

    #define drawCircle(pos, radius, coord) \
      smoothstep(radius + gridSmoothWidth, radius, length(coord - (pos)))
    #define drawSmoothLine(pos, halfWidth, t) \
      smoothstep(halfWidth, 0.0, abs(pos - (t)))
    #define drawCrispLine(pos, halfWidth, t) \
      smoothstep(halfWidth + gridSmoothWidth, halfWidth, abs(pos - (t)))
    #define drawPeriodicLine(freq, width, t) \
      drawCrispLine(freq / 2.0, width, abs(mod(t, freq) - (freq) / 2.0))

    float drawGridLines(float axis) {
      return drawCrispLine(0.0, axisWidth, axis)
           + drawPeriodicLine(majorLineFrequency, majorLineWidth, axis)
           + drawPeriodicLine(minorLineFrequency, minorLineWidth, axis);
    }

    float random(float t) {
      return (cos(t) + cos(t * 1.3 + 1.3) + cos(t * 1.4 + 1.4)) / 3.0;
    }
    
    float hash21(vec2 p) {
      p = fract(p * vec2(123.34, 345.45));
      p += dot(p, p + 34.345);
      return fract(p.x * p.y);
    }

    float getPlasmaY(float x, float horizontalFade, float offset) {
      return random(x * lineFrequency + iTime * lineSpeed) * horizontalFade * lineAmplitude + offset;
    }

    void main() {
      vec2 fragCoord = gl_FragCoord.xy;
      vec2 uv    = fragCoord.xy / iResolution.xy;
      vec2 space = (fragCoord - iResolution.xy / 2.0) / iResolution.x * 2.0 * scale;
      vec2 mouseUv = iMouse / iResolution.xy;
      float mouseDist = distance(uv, mouseUv);
      float mouseField = exp(-mouseDist * 9.0) * iPointerActive;
      float shimmer = 0.88 + 0.12 * sin(iTime * 2.8 + uv.x * 8.0 + uv.y * 4.0);

      float horizontalFade = 1.0 - (cos(uv.x * 6.28) * 0.5 + 0.5);
      float verticalFade   = mix(0.45, 1.0, 1.0 - (cos(uv.y * 6.28) * 0.5 + 0.5));

      space.y += random(space.x * warpFrequency + iTime * warpSpeed) * warpAmplitude * (0.5 + horizontalFade);
      space.x += random(space.y * warpFrequency + iTime * warpSpeed + 2.0) * warpAmplitude * horizontalFade;
      // Pointer bends local flow so visitors can "play" with the waves.
      space.y += sin((uv.x - mouseUv.x) * 18.0 + iTime * 2.5) * mouseField * 0.25;
      space.x += cos((uv.y - mouseUv.y) * 15.0 - iTime * 2.0) * mouseField * 0.18;

      // Light Flowman palette: #E4E7EC → #F5F7FA
      vec4 bgColor1 = vec4(0.945, 0.958, 0.978, 1.0);
      vec4 bgColor2 = vec4(0.985, 0.990, 0.997, 1.0);
      vec4 bg = mix(bgColor1, bgColor2, uv.x);

      // Accumulate line intensity as a scalar (not additive colour)
      float lineAccum = 0.0;

      for (int l = 0; l < linesPerGroup; l++) {
        float normalizedLineIndex = float(l) / float(linesPerGroup);
        float offsetTime     = iTime * offsetSpeed;
        float offsetPosition = float(l) + space.x * offsetFrequency;
        float rand           = random(offsetPosition + offsetTime) * 0.5 + 0.5;
        float halfWidth      = mix(minLineWidth, maxLineWidth, rand * horizontalFade) / 2.0;
        float offset         = random(offsetPosition + offsetTime * (1.0 + normalizedLineIndex))
                             * mix(minOffsetSpread, maxOffsetSpread, horizontalFade);
        float linePosition   = getPlasmaY(space.x, horizontalFade, offset);
        float line           = drawSmoothLine(linePosition, halfWidth, space.y) / 3.5
                             + drawCrispLine(linePosition, halfWidth * 0.30, space.y);

        float circleX        = mod(float(l) + iTime * lineSpeed, 25.0) - 12.0;
        vec2  circlePosition = vec2(circleX, getPlasmaY(circleX, horizontalFade, offset));
        float circle         = drawCircle(circlePosition, 0.01, space) * 4.0;

        lineAccum += (line + circle) * rand;
      }

      // Blend the background toward Flowman blue (#10A7F3) where lines are present.
      // Fades naturally at horizontal and vertical edges — no black vignette.
      float lineStrength = clamp(lineAccum * 0.62, 0.0, 0.96) * horizontalFade * verticalFade;
      lineStrength *= mix(1.0, shimmer * 1.06, 0.25 + mouseField);
      lineStrength += mouseField * 0.08;
      vec4 blueTint  = mix(bg, lineColor, 0.94);
      vec4 fragColor = mix(bg, blueTint, lineStrength);

      // Slow-moving aurora band adds depth without overpowering content.
      float bandCenter = 0.42 + 0.08 * sin(iTime * 0.35 + uv.x * 2.6);
      float auroraBand = exp(-pow((uv.y - bandCenter) * 6.5, 2.0));
      vec4 auroraTint = mix(lineColor, accentColor, 0.55);
      fragColor = mix(fragColor, mix(fragColor, auroraTint, 0.22), auroraBand * 0.35);

      // Subtle micro-sparkle for visual interest.
      float sparkleNoise = hash21(floor(uv * vec2(120.0, 90.0)) + floor(iTime * 0.8));
      float sparkleMask = smoothstep(0.992, 1.0, sparkleNoise) * (0.08 + mouseField * 0.22);
      fragColor.rgb += sparkleMask * vec3(0.12, 0.23, 0.28);

      fragColor.a    = 1.0;
      gl_FragColor   = fragColor;
    }
  `;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');
    if (!gl) { console.warn('WebGL not supported.'); return; }

    // Compile a shader
    const loadShader = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compile error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader   = loadShader(gl.VERTEX_SHADER,   vsSource);
    const fragmentShader = loadShader(gl.FRAGMENT_SHADER, fsSource);
    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Shader link error:', gl.getProgramInfoLog(program));
      return;
    }

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);

    const posLoc = gl.getAttribLocation(program, 'aVertexPosition');
    const resLoc = gl.getUniformLocation(program, 'iResolution');
    const timLoc = gl.getUniformLocation(program, 'iTime');
    const mouseLoc = gl.getUniformLocation(program, 'iMouse');
    const pointerActiveLoc = gl.getUniformLocation(program, 'iPointerActive');
    const pointer = { x: 0, y: 0, active: 0 };

    const getDpr = () => Math.min(window.devicePixelRatio || 1, 2);

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      const dpr = getDpr();
      const w = parent ? parent.offsetWidth  : window.innerWidth;
      const h = parent ? parent.offsetHeight : window.innerHeight;
      canvas.width  = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    const handlePointerMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      const dpr = getDpr();
      pointer.x = (event.clientX - rect.left) * dpr;
      pointer.y = (rect.height - (event.clientY - rect.top)) * dpr;
      pointer.active = 1;
    };

    const handlePointerLeave = () => {
      pointer.active = 0;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerdown', handlePointerMove);
    canvas.addEventListener('pointerleave', handlePointerLeave);
    window.addEventListener('blur', handlePointerLeave);

    let animId;
    const startTime = Date.now();
    const render = () => {
      const t = (Date.now() - startTime) / 1000;
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.uniform1f(timLoc, t);
      gl.uniform2f(mouseLoc, pointer.x, pointer.y);
      gl.uniform1f(pointerActiveLoc, pointer.active);
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(posLoc);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      animId = requestAnimationFrame(render);
    };
    animId = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerdown', handlePointerMove);
      canvas.removeEventListener('pointerleave', handlePointerLeave);
      window.removeEventListener('blur', handlePointerLeave);
      cancelAnimationFrame(animId);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full" />;
};

export default ShaderBackground;
